import { Controller } from "./Controller.js";
import type { GameDirective } from "./GameDirective.js";
import type { PlayerStateController } from "./playerstate/PlayerStateController.js";
import type { DiceController } from "./dice/DiceController.js";
import { PlayerStateDirective } from "./playerstate/PlayerStateDirective.js";
import { DiceDirective } from "./dice/DiceDirective.js";
import type { BoardController } from "./board/BoardController.js";
import { BoardDirective } from "./board/BoardDirective.js";
import type { Directive } from "./Directive.js";
import type { GameSource } from "./source/GameSource.js";
import type { Game } from "../../../royalur/Game.js";
import type { ReadonlyRune } from "../../Rune.js";
import { Rune } from "../../Rune.js";
import type { LobbyStatus } from "./status/LobbyStatus.js";
import type { GamePlayers } from "../../api/game/GamePlayers.js";
import type { GameUpdateEvent } from "./source/GameUpdateEvent.js";
import type { GameThemeType } from "../theme/GameThemeType.js";
import type { ReactionController } from "./reactions/ReactionController.js";
import type { PlayerType } from "../../../royalur/model/PlayerType.js";
import type { ReactionType } from "../ReactionType.js";
import type { Optional } from "../../../util/Optional.js";
import type { ClientError } from "../error/ClientError.js";
import type { ReadonlyRuneWithEvents } from "../../RuneWithEvents.js";


export type WinListener = () => void;


/**
 * Coordinates the interactions within a game.
 */
export abstract class GameController extends Controller<GameDirective> {
    protected readonly playerStateController: PlayerStateController;
    protected readonly reactionController: ReactionController;
    protected readonly diceController: DiceController;
    protected readonly boardController: BoardController;
    protected readonly source: GameSource;
    protected readonly theme: Rune<GameThemeType>;

    private playerStateDirectiveSinks: number = 0;
    private diceDirectiveSinks: number = 0;
    private boardDirectiveSinks: number = 0;

    private processingNewGameDirective: boolean = false;

    protected constructor(
        playerStateController: PlayerStateController,
        reactionController: ReactionController,
        diceController: DiceController,
        boardController: BoardController,
        source: GameSource,
        defaultThemeType: GameThemeType,
    ) {
        super(source.getControllerMaxDirectives());

        this.playerStateController = playerStateController;
        this.reactionController = reactionController;
        this.diceController = diceController;
        this.boardController = boardController;
        this.source = source;
        this.theme = new Rune<GameThemeType>(defaultThemeType);
    }

    /**
     * Must be called at the end of the constructor of subclasses.
     */
    postConstructor() {
        // Has to be called because the self-listener for
        // the directive change hasn't been added yet.
        this.processNewGameDirective(this.getActiveDirective());
    }

    getGameSource(): GameSource {
        return this.source;
    }

    getTheme(): Rune<GameThemeType> {
        return this.theme;
    }

    getGame(): ReadonlyRuneWithEvents<Game, GameUpdateEvent> {
        return this.source.game;
    }

    getLobbyStatus(): ReadonlyRune<LobbyStatus> {
        return this.source.lobbyStatus;
    }

    getPlayers(): ReadonlyRune<GamePlayers> {
        return this.source.players;
    }

    getError(): ReadonlyRune<Optional<ClientError>> {
        return this.source.error;
    }

    override setup(): () => void {
        const cleanup: (() => void)[] = [];

        // This controller reacts to itself.
        cleanup.push(this.subscribeToActiveDirective(
            directive => this.processNewGameDirective(directive),
        ));

        cleanup.push(this.playerStateController.subscribeToActiveDirective(
            directive => this.onNewPlayerStateDirective(directive),
        ));
        cleanup.push(this.diceController.subscribeToActiveDirective(
            directive => this.onNewDiceDirective(directive),
        ));
        cleanup.push(this.boardController.subscribeToActiveDirective(
            directive => this.onNewBoardDirective(directive),
        ));

        cleanup.push(this.source.setup());

        return () => cleanup.forEach(fn => fn());
    }

    /**
     * If we have a directive sink, then we wait for its animations to complete.
     */
    registerPlayerStateDirectiveSink(): () => void {
        this.playerStateDirectiveSinks += 1;
        return () => {
            this.playerStateDirectiveSinks -= 1;
        };
    }

    /**
     * If we have a directive sink, then we wait for its animations to complete.
     */
    registerDiceDirectiveSink(): () => void {
        this.diceDirectiveSinks += 1;
        return () => {
            this.diceDirectiveSinks -= 1;
        };
    }

    /**
     * If we have a directive sink, then we wait for its animations to complete.
     */
    registerBoardDirectiveSink(): () => void {
        this.boardDirectiveSinks += 1;
        return () => {
            this.boardDirectiveSinks -= 1;
        };
    }

    override onDiscardedDirectives(directives: GameDirective[]): void {
        for (const directive of directives) {
            this.boardController.discardDirectives(directive.boardDirectives);
            this.diceController.discardDirectives(directive.diceDirectives);
            this.playerStateController.discardDirectives(directive.playerStateDirectives);
        }
    }

    /**
     * Translates game directives in this controller to child directives
     * for the board, dice, and player state.
     */
    private processNewGameDirective(directive: GameDirective) {
        this.processingNewGameDirective = true;
        try {
            this.boardController.pushDirectives(directive.boardDirectives);
            this.diceController.pushDirectives(directive.diceDirectives);
            this.playerStateController.pushDirectives(directive.playerStateDirectives);
        } finally {
            this.processingNewGameDirective = false;
            this.processFinishDirectiveCheck();
        }
    }

    addWinListener(_listener: WinListener): () => void {
        throw new Error("This game controller does not support addWinListener");
    }

    rematch() {
        throw new Error("This game controller does not support rematch");
    }

    cancelRematch() {
        throw new Error("This game controller does not support cancelRematch");
    }

    handleReaction(_player: PlayerType | null, _reaction: ReactionType) {
        throw new Error("This game controller does not support handleReaction");
    }

    handleAnimateDiceRolling() {
        throw new Error("This game controller does not support handleAnimateDiceRolling");
    }

    private isChildActiveOrQueued(childDirective: Directive): boolean {
        if (childDirective instanceof BoardDirective) {
            return (
                this.boardDirectiveSinks > 0
                && this.boardController.isActiveOrQueued(childDirective)
            );
        }
        if (childDirective instanceof DiceDirective) {
            return (
                this.diceDirectiveSinks > 0
                && this.diceController.isActiveOrQueued(childDirective)
            );
        }
        if (childDirective instanceof PlayerStateDirective) {
            return (
                this.playerStateDirectiveSinks > 0
                && this.playerStateController.isActiveOrQueued(childDirective)
            );
        }
        throw new Error("Unrecognised child directive " + JSON.stringify(childDirective));
    }

    private areActiveChildDirectivesRemaining(directive: GameDirective): boolean {
        for (const childDirective of directive.getAllDirectives()) {
            if (!childDirective.isLimboDirective() && this.isChildActiveOrQueued(childDirective))
                return true;
        }
        return false;
    }

    processFinishDirectiveCheck() {
        const directive = this.getActiveDirective();
        if (directive.isLimboDirective())
            return;

        if (!this.areActiveChildDirectivesRemaining(directive)) {
            this.popActiveDirective(directive);
        }
    }

    onNewPlayerStateDirective(_directive: PlayerStateDirective) {
        if (this.processingNewGameDirective)
            return;
        this.processFinishDirectiveCheck();
    }

    onNewDiceDirective(_directive: DiceDirective) {
        if (this.processingNewGameDirective)
            return;
        this.processFinishDirectiveCheck();
    }

    onNewBoardDirective(_directive: BoardDirective) {
        if (this.processingNewGameDirective)
            return;
        this.processFinishDirectiveCheck();
    }
}
